import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Refresh, Delete, Edit, ChevronLeft, ChevronRight } from '@mui/icons-material';
import {
  getAddOnServices,
  createAddOnService,
  updateAddOnService,
  deleteAddOnService,
  getServiceTypes
} from '../../services/fulfillment';

const AddOns = () => {
  const { t } = useTranslation(['fulfillment', 'common']);
  const [addons, setAddons] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    service_type: null,
    fee_per_item: '',
    fee_per_package: '',
    is_required: false,
    is_active: true,
    description: ''
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = Math.ceil(totalItems / rowsPerPage) || 1;

  const loadServiceTypes = useCallback(async () => {
    try {
      const response = await getServiceTypes({ page_size: 100 });
      if (response.results) {
        setServiceTypes(response.results);
      } else if (Array.isArray(response)) {
        setServiceTypes(response);
      }
    } catch (error) {
      console.error('Error loading service types:', error);
    }
  }, []);

  const loadAddOns = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage
      };
      const response = await getAddOnServices(params);
      if (response.results) {
        setAddons(response.results);
        setTotalItems(response.count || 0);
      } else if (Array.isArray(response)) {
        setAddons(response);
        setTotalItems(response.length);
      }
    } catch (error) {
      console.error('Error loading add-ons:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:addons.messages.failed'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, t]);

  useEffect(() => {
    loadServiceTypes();
  }, [loadServiceTypes]);

  useEffect(() => {
    loadAddOns();
  }, [loadAddOns]);

  const handleOpen = (item = null) => {
    if (item) {
      setEditId(item.sid);
      setForm({
        name: item.name || '',
        code: item.code || '',
        service_type: item.service_type?.sid || null,
        fee_per_item: item.fee_per_item || '',
        fee_per_package: item.fee_per_package || '',
        is_required: item.is_required || false,
        is_active: item.is_active !== undefined ? item.is_active : true,
        description: item.description || ''
      });
    } else {
      setEditId(null);
      setForm({
        name: '',
        code: '',
        service_type: null,
        fee_per_item: '',
        fee_per_package: '',
        is_required: false,
        is_active: true,
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditId(null);
    setForm({
      name: '',
      code: '',
      service_type: null,
      fee_per_item: '',
      fee_per_package: '',
      is_required: false,
      is_active: true,
      description: ''
    });
  };

  const handleSave = async () => {
    if (!form.name || !form.code) {
      setSnackbar({
        open: true,
        message: t('common:validation.required'),
        severity: 'error'
      });
      return;
    }

    if (!form.fee_per_item && !form.fee_per_package) {
      setSnackbar({
        open: true,
        message: 'Please provide either fee per item or fee per package',
        severity: 'error'
      });
      return;
    }

    try {
      const payload = {
        name: form.name,
        code: form.code,
        service_type_sid: form.service_type || null,
        fee_per_item: form.fee_per_item ? parseFloat(form.fee_per_item) : null,
        fee_per_package: form.fee_per_package ? parseFloat(form.fee_per_package) : null,
        is_required: form.is_required,
        is_active: form.is_active,
        description: form.description
      };

      if (editId) {
        await updateAddOnService(editId, payload);
        setSnackbar({
          open: true,
          message: t('fulfillment:addons.messages.updated'),
          severity: 'success'
        });
      } else {
        await createAddOnService(payload);
        setSnackbar({
          open: true,
          message: t('fulfillment:addons.messages.created'),
          severity: 'success'
        });
      }
      handleClose();
      loadAddOns();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:addons.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteDialog({ open: true, item });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteAddOnService(deleteDialog.item.sid);
      setSnackbar({
        open: true,
        message: t('fulfillment:addons.messages.deleted'),
        severity: 'success'
      });
      setDeleteDialog({ open: false, item: null });
      loadAddOns();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:addons.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: t('fulfillment:table.sn'), width: 80, sortable: false, filterable: false },
    { field: 'name', headerName: t('fulfillment:addons.name'), flex: 1, minWidth: 150 },
    { field: 'code', headerName: t('fulfillment:addons.code'), flex: 1, minWidth: 120 },
    {
      field: 'service_type',
      headerName: t('fulfillment:addons.serviceType'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.service_type?.name || 'All Services'}
        </Typography>
      )
    },
    {
      field: 'fee_per_item',
      headerName: t('fulfillment:addons.feePerItem'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? `¥${Number(params.value).toFixed(2)}` : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'fee_per_package',
      headerName: t('fulfillment:addons.feePerPackage'),
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? `¥${Number(params.value).toFixed(2)}` : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'is_required',
      headerName: t('fulfillment:addons.isRequired'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Yes' : 'No'}
          size="small"
          sx={{
            backgroundColor: params.value ? '#4CAF50' : '#F44336',
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'is_active',
      headerName: t('fulfillment:addons.isActive'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common:status.active') : t('common:status.inactive')}
          size="small"
          sx={{
            backgroundColor: params.value ? '#4CAF50' : '#F44336',
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: t('fulfillment:table.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('common:edit')}>
            <IconButton color="primary" onClick={() => handleOpen(params.row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <IconButton color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('fulfillment:addons.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadAddOns}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('common:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={() => handleOpen()}
          >
            {t('fulfillment:addons.add')}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {addons.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('fulfillment:table.noData')}
                </Typography>
                <Button variant="outlined" onClick={loadAddOns}>
                  {t('common:refresh')}
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(addons, page, rowsPerPage)}
                columns={columns}
                getRowId={(row) => row.sid}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1000,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-end' },
        mt: 3,
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('common:pagination.page')} {totalPages === 0 ? 0 : page + 1} {t('common:pagination.of')} {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('common:pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('common:pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('common:pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {editId ? t('fulfillment:addons.edit') : t('fulfillment:addons.add')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('fulfillment:addons.name')}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={t('fulfillment:addons.code')}
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              required
              fullWidth
              helperText={t('common:codeHelper')}
            />
            <FormControl fullWidth>
              <InputLabel>{t('fulfillment:addons.serviceType')}</InputLabel>
              <Select
                value={form.service_type || ''}
                onChange={(e) => setForm({ ...form, service_type: e.target.value || null })}
                label={t('fulfillment:addons.serviceType')}
              >
                <MenuItem value="">All Services</MenuItem>
                {serviceTypes.map((st) => (
                  <MenuItem key={st.sid} value={st.sid}>
                    {st.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('fulfillment:addons.feePerItem')}
                  type="number"
                  value={form.fee_per_item}
                  onChange={(e) => setForm({ ...form, fee_per_item: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="RMB per item"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('fulfillment:addons.feePerPackage')}
                  type="number"
                  value={form.fee_per_package}
                  onChange={(e) => setForm({ ...form, fee_per_package: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="RMB per package"
                />
              </Grid>
            </Grid>
            <TextField
              label={t('fulfillment:addons.description')}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_required}
                  onChange={(e) => setForm({ ...form, is_required: e.target.checked })}
                />
              }
              label={t('fulfillment:addons.isRequired')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label={t('fulfillment:addons.isActive')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common:cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {t('fulfillment:common.confirmDelete')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('fulfillment:common.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddOns;
